import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface SeriesStats {
  pendingSuggestions: number;
  verifiedSeries: number;
  totalBooksWithSeries: number;
  seriesWithGaps: number;
  coveragePercent: number;
}

export function useSeriesStats() {
  return useQuery({
    queryKey: ['series-stats'],
    queryFn: async (): Promise<SeriesStats> => {
      // Count pending suggestions
      const { count: pendingCount } = await supabase
        .from('series_suggestions')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending');

      // Count unique verified series from books table
      const { data: seriesData } = await supabase
        .from('books')
        .select('series_name')
        .not('series_name', 'is', null);

      const uniqueSeries = new Set(seriesData?.map(b => b.series_name?.toLowerCase()) || []);

      // Count total books with series data
      const { count: booksWithSeries } = await supabase
        .from('books')
        .select('*', { count: 'exact', head: true })
        .not('series_name', 'is', null);

      // Count series with gaps (series where we have some books but not all)
      // This is more complex - we check series_suggestions that have gaps
      const { data: suggestionsWithGaps } = await supabase
        .from('series_suggestions')
        .select(`
          id,
          total_books_expected,
          series_books (
            is_found
          )
        `)
        .eq('status', 'approved');

      let gapsCount = 0;
      for (const s of suggestionsWithGaps || []) {
        const foundCount = s.series_books?.filter(b => b.is_found).length || 0;
        if (s.total_books_expected && foundCount < s.total_books_expected) {
          gapsCount++;
        }
      }

      // Calculate coverage (books with series / estimated total books in all series)
      const totalExpected = suggestionsWithGaps?.reduce((sum, s) => sum + (s.total_books_expected || 0), 0) || 0;
      const coveragePercent = totalExpected > 0 
        ? Math.round((booksWithSeries || 0) / totalExpected * 100)
        : 0;

      return {
        pendingSuggestions: pendingCount || 0,
        verifiedSeries: uniqueSeries.size,
        totalBooksWithSeries: booksWithSeries || 0,
        seriesWithGaps: gapsCount,
        coveragePercent: Math.min(coveragePercent, 100), // Cap at 100%
      };
    },
    staleTime: 30 * 1000, // Cache for 30 seconds
  });
}
